import { useEffect, useMemo, useRef, useState } from "react";
import type { DetailsFormLocationKey, DetailsFormValues } from "../DetailsForm";
import { useCompanyInfo, useUpdateCompany } from "../../../../../../../shared/hooks/useCompany";

type FieldKey = keyof DetailsFormValues;

export type FieldDef = {
  key: FieldKey;
  label: string;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
};

const getMockSchema = (locationKey: DetailsFormLocationKey): FieldDef[] => {
  void locationKey;

  return [
    { key: "accountHolderName", label: "Account holder name", required: true },
    { key: "routingNumber", label: "Routing number", required: true },
    { key: "accountNumber", label: "Account number", required: false },
    { key: "address", label: "Address", required: false },
  ];
};

type UseDetailsFormArgs = {
  locationKey?: DetailsFormLocationKey;
  values: DetailsFormValues;
  onChange: (next: DetailsFormValues) => void;
};

type SnackbarState = {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info" | "warning";
};

const pickByKeys = (values: DetailsFormValues, keys: FieldKey[]) => {
  const out: Partial<DetailsFormValues> = {};
  for (const k of keys) out[k] = values[k];
  return out;
};

const stableStringify = (obj: Record<string, unknown>) => {
  const sorted = Object.keys(obj)
    .sort()
    .reduce<Record<string, unknown>>((acc, k) => {
      acc[k] = obj[k];
      return acc;
    }, {});
  return JSON.stringify(sorted);
};

export const useDetailsForm = ({ locationKey = "default", values, onChange }: UseDetailsFormArgs) => {
  const fields = useMemo(() => getMockSchema(locationKey), [locationKey]);
  const fieldKeys = useMemo(() => fields.map((f) => f.key), [fields]);

  const storageKey = useMemo(() => `bankTransfer.details.${locationKey}`, [locationKey]);

  // baseline = last saved (from storage initially, then updated after save)
  const baselineRef = useRef<Partial<DetailsFormValues>>({});
  const hydratedRef = useRef(false);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  useEffect(() => {
    hydratedRef.current = false;
  }, [storageKey]);

  const { data: company, isError } = useCompanyInfo();
  const updateCompanyMutation = useUpdateCompany();

  useEffect(() => {
    if (hydratedRef.current) return;
    if (company === undefined && !isError) return;
    hydratedRef.current = true;

    try {
      if (company && Array.isArray(company.bankTransfer) && company.bankTransfer.length > 0) {
        const companyDetails = company.bankTransfer[0];
        const hydrated = {
          accountHolderName: companyDetails.accountHolderName || "",
          routingNumber: companyDetails.routingNumber || "",
          accountNumber: companyDetails.accountNumber || "",
          address: companyDetails.address || "",
        };
        baselineRef.current = pickByKeys(hydrated, fieldKeys);
        onChange(hydrated);
        localStorage.setItem(storageKey, JSON.stringify(hydrated));
        return;
      }

      const raw = localStorage.getItem(storageKey);

      if (raw) {
        const parsed = JSON.parse(raw) as Partial<DetailsFormValues> | null;
        if (parsed && typeof parsed === "object") {
          const hydrated = { ...values, ...parsed };
          baselineRef.current = pickByKeys(hydrated, fieldKeys);
          onChange(hydrated);
          return;
        }
      }

      // if nothing in storage (or invalid), baseline is whatever we have now
      baselineRef.current = pickByKeys(values, fieldKeys);
    } catch {
      baselineRef.current = pickByKeys(values, fieldKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, isError, storageKey, fieldKeys]);

  const setField = (key: FieldKey, value: string) => {
    onChange({ ...values, [key]: value });
  };

  const istouch = useMemo(() => {
    const current = stableStringify(pickByKeys(values, fieldKeys) as Record<string, unknown>);
    const baseline = stableStringify(pickByKeys(baselineRef.current as DetailsFormValues, fieldKeys) as Record<string, unknown>);
    return current !== baseline;
  }, [values, fieldKeys]);

  const saveToStorage = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(values));
      baselineRef.current = pickByKeys(values, fieldKeys);

      updateCompanyMutation.mutate({
        bankTransfer: [
          {
            routingNumber: values.routingNumber || "",
            accountHolderName: values.accountHolderName || "",
            accountNumber: values.accountNumber || "",
            address: values.address || "",
          },
        ],
      });

      return true;
    } catch {
      return false;
    }
  };

  const save = () => {
    if (!istouch) return false;

    const ok = saveToStorage();
    setSnackbar(
      ok ? { open: true, message: "Details saved.", severity: "success" } : { open: true, message: "Could not save details.", severity: "error" },
    );
    return ok;
  };

  return {
    fields,
    setField,
    storageKey,

    // state for button
    istouch,

    // actions
    saveToStorage,
    save,

    // snackbar
    snackbar,
    closeSnackbar,
  };
};
